import { useState, useEffect } from 'react';
import firebase from 'firebase/app';

const LIMIT = 400;

function toStartOfDay(date) {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function toEndOfDay(date) {
    if (!date) return null;
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

export function useTeamLeavers({ startDate, endDate, limit = LIMIT } = {}) {
    const [leavers, setLeavers] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const db = firebase.firestore();
        let query = db.collectionGroup('members').where('isDeleted', '==', true);

        const normalizedStart = toStartOfDay(startDate);
        const normalizedEnd = toEndOfDay(endDate);

        if (normalizedStart) {
            query = query.where('updatedAt', '>=', firebase.firestore.Timestamp.fromDate(normalizedStart));
        }
        if (normalizedEnd) {
            query = query.where('updatedAt', '<=', firebase.firestore.Timestamp.fromDate(normalizedEnd));
        }

        query = query.orderBy('updatedAt', 'desc').limit(limit);

        const unsubscribe = query.onSnapshot(
            async (snapshot) => {
                const list = [];
                snapshot.forEach((doc) => {
                    const data = doc.data();
                    const pathParts = doc.ref.path.split('/');
                    // Path: teams/{teamId}/members/{memberId} (top-level teams collection)
                    const teamIdFromPath = pathParts[0] === 'teams' && pathParts.length >= 2 ? pathParts[1] : null;
                    const teamId = data.teamId || teamIdFromPath;
                    list.push({
                        id: doc.id,
                        memberId: doc.id,
                        teamId,
                        teamOwnerClientId: data.teamOwnerClientId || null,
                        ...data,
                    });
                });
                // Fetch teamOwnerClientId from team docs (not in member path)
                const teamIds = [...new Set(list.map((i) => i.teamId).filter(Boolean))];
                if (teamIds.length > 0) {
                    const teamSnaps = await Promise.all(
                        teamIds.map((tid) => db.collection('teams').doc(tid).get())
                    );
                    const ownerByTeam = {};
                    const membersByTeam = {};
                    const tapBehaviourByTeam = {};
                    teamSnaps.forEach((snap, idx) => {
                        if (snap.exists) {
                            const d = snap.data();
                            ownerByTeam[teamIds[idx]] = d.teamOwnerClientId || null;
                            membersByTeam[teamIds[idx]] = d.teamNumberOfMembers;
                            tapBehaviourByTeam[teamIds[idx]] = d.teamTapBehaviour || null;
                        }
                    });
                    list.forEach((item) => {
                        item.teamOwnerClientId = item.teamOwnerClientId || ownerByTeam[item.teamId];
                        item.teamNumberOfMembers = item.teamNumberOfMembers ?? membersByTeam[item.teamId];
                        item.teamTapBehaviour = item.teamTapBehaviour ?? tapBehaviourByTeam[item.teamId];
                    });
                }
                setLeavers(list);
                setLoading(false);
            },
            (err) => {
                console.error('useTeamLeavers error:', err);
                setLeavers([]);
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [
        startDate ? new Date(startDate).getTime() : null,
        endDate ? new Date(endDate).getTime() : null,
        limit,
    ]);

    return [loading, leavers];
}
